import type { SelectOptionWithMetadata } from '@lib/responses/configurator';
import { ConfiguratorSingleChoiceValue } from '@snap-types/features/configurator-v1/types/snapshot';
import {
  useConfiguratorStore,
  useDataSource,
} from '@ui/features/configurator/hooks';
import { isFieldDisabled } from '@ui/features/configurator/store/helpers/field-helpers';
import type { ConfiguratorFieldType } from '@ui/features/configurator/types';
import {
  useCombobox,
  type UseComboboxInputValueChange,
  type UseComboboxSelectedItemChange,
} from 'downshift';
import { debounce } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Combobox } from '../Combobox';
import { ConfiguratorInputWrapper } from './InputWrapper';
import type { ConfiguratorInputWrapperWidth } from './types';

export type SelectedItem<Meta = Record<string, unknown>> =
  SelectOptionWithMetadata<string, Meta>;

const SCROLL_DEBOUNCE_WAIT = 50;
const INPUT_CHANGE_DEBOUNCE_WAIT = 200;

const EMPTY_DATA = [] as const;

function BaseSingleChoiceComponent<
  Meta extends Record<string, unknown>,
  Params extends Record<string, unknown>,
>({
  field,
  isPending,
  isDisabled,
  onItemSelected,
  params,
  ...widths
}: ConfiguratorInputWrapperWidth & {
  field: ConfiguratorFieldType;
  isPending?: boolean;
  isDisabled?: boolean;
  params?: Params;
  onItemSelected: (item: SelectedItem<Meta> | null) => void;
}) {
  const [queryEnabled, setQueryEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');

  const { fields } = useConfiguratorStore();
  const selectedOption = field.value as ConfiguratorSingleChoiceValue<Meta>;

  const { data, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useDataSource<Meta, Params>(
      field,
      fields,
      queryEnabled,
      searchQuery,
      params,
    );

  const handleSelectedItemChange = useCallback(
    async (
      changes: UseComboboxSelectedItemChange<SelectedItem<Meta> | null>,
    ) => {
      setInputValue(changes.selectedItem?.label ?? '');
      onItemSelected(changes.selectedItem);
    },
    [onItemSelected],
  );

  const debouncedSetSearchQuery = useMemo(
    () => debounce(setSearchQuery, INPUT_CHANGE_DEBOUNCE_WAIT),
    [],
  );

  const handleInputValueChange = useCallback(
    (changes: UseComboboxInputValueChange<SelectedItem<Meta>>) => {
      if (changes.type === useCombobox.stateChangeTypes.InputChange) {
        setInputValue(changes.inputValue);
        debouncedSetSearchQuery(changes.inputValue.trim());
      }

      if (
        changes.type === useCombobox.stateChangeTypes.InputBlur ||
        changes.type === useCombobox.stateChangeTypes.FunctionReset
      ) {
        setSearchQuery('');
        setInputValue(changes.selectedItem ? changes.selectedItem.label : '');
      }
    },
    [debouncedSetSearchQuery],
  );

  const handleScrollToBottom = useMemo(
    () =>
      debounce(() => {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      }, SCROLL_DEBOUNCE_WAIT),
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    setInputValue(selectedOption?.label ?? '');
  }, [selectedOption]);

  const handleFocus = useCallback(() => setQueryEnabled(true), []);

  return (
    <ConfiguratorInputWrapper field={field} {...widths}>
      <Combobox
        inputId={field.fieldKey}
        isLoading={isFetching || isPending}
        selectedItem={selectedOption}
        inputValue={inputValue}
        placeholder={field.placeholder}
        items={data?.data ?? (EMPTY_DATA as [])}
        isDisabled={isDisabled || isFieldDisabled(field)}
        onFocus={handleFocus}
        onSelectedItemChange={handleSelectedItemChange}
        onInputValueChange={handleInputValueChange}
        onScrollToBottom={
          field.inputSource?.source_type === 'dynamic'
            ? handleScrollToBottom
            : undefined
        }
      />
    </ConfiguratorInputWrapper>
  );
}

/**
 * Searchable select for Configurator forms.
 *
 * Requires ConfiguratorStoreProvider in a parent component.
 *
 * Ref: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
 */
export const ConfiguratorBaseSingleChoiceInput = memo(
  BaseSingleChoiceComponent,
) as typeof BaseSingleChoiceComponent;
